import React, { useState, useEffect, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import Slider from 'react-slick';
import { getAllProducts, getVariantsByProduct } from '../api';

const toSlug = (name) =>
    name?.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || '';

const API_BASE_URL = process.env.REACT_APP_API_URL;

const getCustomerId = () => {
    const customer = JSON.parse(localStorage.getItem('customer'));
    return customer?.id || null;
};

const normalizeVariantId = (variantId) => {
    if (variantId === null || variantId === undefined) return 'default';
    if (variantId === 'default') return 'default';
    return String(variantId);
};

const makeCartId = (productId, variantId) =>
    `${productId}-${normalizeVariantId(variantId)}`;

const getCartedQty = (productId, variantId) => {
    const cart   = JSON.parse(localStorage.getItem('cart')) || [];
    const cartId = makeCartId(productId, variantId);
    const item   = cart.find(i => i.cartId === cartId);
    return item ? item.quantity : 0;
};

// ── Orange hover arrows ──────────────────────────────────────────────────────
const SampleNextArrow = memo(({ className, onClick }) => (
    <button type="button" onClick={onClick}
        className={`${className} na-slick-arrow na-slick-next`}>
        <i className="ph ph-caret-right" />
    </button>
));

const SamplePrevArrow = memo(({ className, onClick }) => (
    <button type="button" onClick={onClick}
        className={`${className} na-slick-arrow na-slick-prev`}>
        <i className="ph ph-caret-left" />
    </button>
));

const SIZE_UNITS = ['S', 'M', 'L', 'XL', 'XXL'];

const getVariantLabel = (variant) => {
    if (!variant) return 'Default';
    const name = (variant.variantName || '').trim().toUpperCase();
    const unit = (variant.unit || '').trim().toUpperCase();
    if (SIZE_UNITS.includes(unit)) return unit;
    if (SIZE_UNITS.includes(name)) return name;
    const n = (variant.variantName || '').trim();
    const u = (variant.unit || '').trim();
    if (n && u) return `${n} ${u}`;
    return n || u || 'Default';
};

const ArrivalProductCard = ({ product, handleProductClick, handleAddToCart, handleToggleWishlist, wishlistIds }) => {
    const [selectedVariant, setSelectedVariant] = useState(null);
    const [cartVersion, setCartVersion] = useState(0);

    useEffect(() => {
        const onStorage = () => setCartVersion(v => v + 1);
        window.addEventListener('storage', onStorage);
        return () => window.removeEventListener('storage', onStorage);
    }, []);

    useEffect(() => {
        if (product.variants && product.variants.length > 0) {
            setSelectedVariant(product.variants[0]);
        } else {
            setSelectedVariant({
                id: 'default', variantName: 'Default', unit: '',
                mrp:        parseFloat(product.mrp)        || 0,
                salesPrice: parseFloat(product.salesPrice) || 0,
                stock:      999,
            });
        }
    }, [product]);

    if (!selectedVariant) return null;

    const salesPrice      = parseFloat(selectedVariant.salesPrice) || parseFloat(product.salesPrice) || 0;
    const mrp             = parseFloat(selectedVariant.mrp)        || parseFloat(product.mrp)        || 0;
    const discount        = mrp > salesPrice ? Math.round(((mrp - salesPrice) / mrp) * 100) : 0;
    const hasRealVariants = product.variants && product.variants.length > 0;
    const isWishlisted    = wishlistIds.includes(product.id);
    const imgSrc          = product.image ? `${API_BASE_URL}/${product.image}` : '/placeholder-img.png';

    const stockQty       = parseInt(selectedVariant?.stock ?? 999);
    const isOutOfStock   = stockQty === 0;
    const isLowStock     = stockQty > 0 && stockQty <= 5;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const cartedQty      = selectedVariant ? getCartedQty(product.id, selectedVariant.id) : 0;
    const remainingStock = Math.max(0, stockQty - cartedQty);
    const isFullyCarted  = !isOutOfStock && remainingStock === 0;
    const addDisabled    = isOutOfStock || isFullyCarted;

    return (
        <div style={{ padding: '10px 8px', height: '100%' }}>
            <div className="na-card" style={{ height: '100%', display: 'flex', flexDirection: 'column', opacity: addDisabled ? 0.85 : 1 }}>
                <div className="na-card__img-wrap" onClick={() => handleProductClick(product)}>
                    <img src={imgSrc} alt={product.productName} />
                    {addDisabled
                        ? <span className="na-discount-badge" style={{ background: '#B91C1C' }}>Out of Stock</span>
                        : discount > 0 && <span className="na-discount-badge" style={{ background: '#C0392B' }}>-{discount}%</span>
                    }
                    <button
                        className={`na-wish-btn ${isWishlisted ? 'active' : ''}`}
                        onClick={(e) => { e.stopPropagation(); handleToggleWishlist(e, product, selectedVariant); }}>
                        <i className={`ph${isWishlisted ? '-fill' : ''} ph-heart`} />
                    </button>
                </div>
                <div className="na-card__body">
                    <p className="na-title" onClick={() => handleProductClick(product)}>
                        {product.productName}
                    </p>
                    <div className="na-stars">
                        <i className="ph-fill ph-star" /><i className="ph-fill ph-star" />
                        <i className="ph-fill ph-star" /><i className="ph-fill ph-star" />
                        <i className="ph ph-star" />
                    </div>
                    {hasRealVariants && (
                        <div className="na-variants">
                            {product.variants.map((v) => {
                                const isActive  = String(v.id) === String(selectedVariant.id);
                                const vStock    = parseInt(v.stock ?? 999);
                                const vCarted   = getCartedQty(product.id, v.id);
                                const vDisabled = vStock === 0 || vCarted >= vStock;
                                return (
                                    <button key={v.id} type="button"
                                        onClick={(e) => { e.stopPropagation(); if (!vDisabled) setSelectedVariant(v); }}
                                        className={`na-vbtn ${isActive ? 'na-vbtn--active' : ''}`}
                                        style={{ opacity: vDisabled ? 0.45 : 1 }}>
                                        {getVariantLabel(v)}
                                        {vDisabled && <span style={{ fontSize: 8, marginLeft: 2, color: '#B91C1C' }}>✕</span>}
                                    </button>
                                );
                            })}
                        </div>
                    )}
                    <div className="na-bottom">
                        <div className="na-price-row">
                            <span className="na-price-now">₹{salesPrice}</span>
                            {mrp > salesPrice && <span className="na-price-old">₹{mrp}</span>}
                            {!addDisabled && mrp > salesPrice && (
                                <span className="na-save-pill">Save ₹{Math.round(mrp - salesPrice)}</span>
                            )}
                        </div>
                        {isLowStock && !isFullyCarted && (
                            <span className="rc-stock-badge" style={{ color: '#C2410C', background: '#FFF7ED', border: '1px solid #FED7AA' }}>
                                Only {remainingStock} left!
                            </span>
                        )}
                        <button
                            className="na-btn-cart"
                            disabled={addDisabled}
                            onClick={(e) => !addDisabled && handleAddToCart(e, product, selectedVariant, getVariantLabel(selectedVariant))}
                            style={{
                                background: addDisabled ? '#9CA3AF' : '#F57C00',
                                cursor:     addDisabled ? 'not-allowed' : 'pointer',
                                opacity:    addDisabled ? 0.7 : 1,
                            }}
                        >
                            <i className="ph ph-shopping-cart-simple" />
                            {addDisabled ? 'Out of Stock' : 'Add to Cart'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

const NewArrivalOne = () => {
    const navigate = useNavigate();
    const [products,    setProducts]    = useState([]);
    const [loading,     setLoading]     = useState(true);
    const [wishlistIds, setWishlistIds] = useState([]);
    const [toast,       setToast]       = useState({ show: false, message: '', type: '' });

    useEffect(() => {
        const fetchArrivals = async () => {
            try {
                const res         = await getAllProducts();
                const arrivalOnly = res.data.filter(p => p.isNewArrival === true || String(p.isNewArrival) === 'true');
                const productsWithVariants = await Promise.all(
                    arrivalOnly.map(async (product) => {
                        try {
                            const variantsRes = await getVariantsByProduct(product.id);
                            return { ...product, variants: variantsRes.data || [] };
                        } catch {
                            return { ...product, variants: [] };
                        }
                    })
                );
                setProducts(productsWithVariants);
                setLoading(false);
            } catch (err) {
                console.error('Error loading products', err);
                setLoading(false);
            }
        };
        fetchArrivals();

        const wl = JSON.parse(localStorage.getItem('wishlist')) || [];
        setWishlistIds(wl.map(i => i.id));
        const handleStorage = () => {
            const wl2 = JSON.parse(localStorage.getItem('wishlist')) || [];
            setWishlistIds(wl2.map(i => i.id));
        };
        window.addEventListener('storage', handleStorage);
        return () => window.removeEventListener('storage', handleStorage);
    }, []);

    const showToast = (message, type = 'success') => {
        setToast({ show: true, message, type });
        setTimeout(() => setToast({ show: false, message: '', type: '' }), 3000);
    };

    const handleProductClick = (product) => {
        const slug = toSlug(product.productName);
        navigate(`/product/${slug}`, {
            state: {
                product: {
                    ...product,
                    name:     product.productName,
                    price:    parseFloat(product.salesPrice) || 0,
                    oldPrice: parseFloat(product.mrp)        || 0,
                    img:      product.image ? `${API_BASE_URL}/${product.image}` : '/placeholder-img.png',
                }
            }
        });
    };

    const handleAddToCart = async (e, prod, variant, variantLabel) => {
        e.stopPropagation();

        // ── Guest check ──────────────────────────────────────────────────────
        const customerId = getCustomerId();
        if (!customerId) {
            showToast('Please login to add items to your cart!', 'warn');
            return;
        }

        const stockQty     = parseInt(variant?.stock ?? 999);
        const isOutOfStock = stockQty === 0;
        if (isOutOfStock) return;

        const cartId       = makeCartId(prod.id, variant.id);
        const existingCart = JSON.parse(localStorage.getItem('cart')) || [];
        const existingIdx  = existingCart.findIndex(item => item.cartId === cartId);

        const currentCarted = existingIdx !== -1 ? existingCart[existingIdx].quantity : 0;
        if (currentCarted >= stockQty) {
            showToast(`Only ${stockQty} in stock — you already have ${currentCarted} in your cart!`, 'info');
            return;
        }

        if (existingIdx !== -1) {
            existingCart[existingIdx].quantity = Math.min(existingCart[existingIdx].quantity + 1, stockQty);
            existingCart[existingIdx].stock    = stockQty;
            showToast(`"${prod.productName} ${variantLabel}" updated in cart!`, 'info');
        } else {
            existingCart.push({
                cartId,
                id:          prod.id,
                variantId:   normalizeVariantId(variant.id),
                name:        prod.productName,
                variantName: variant.variantName || '',
                unit:        variant.unit || '',
                weight:      variantLabel,
                price:       parseFloat(variant.salesPrice) || parseFloat(prod.salesPrice) || 0,
                img:         prod.image ? `${API_BASE_URL}/${prod.image}` : '/placeholder-img.png',
                quantity:    1,
                stock:       stockQty,
            });
            showToast(`"${prod.productName} ${variantLabel}" added to cart!`, 'success');
        }
        localStorage.setItem('cart', JSON.stringify(existingCart));
        window.dispatchEvent(new Event('storage'));

        try {
            const res = await fetch(`${API_BASE_URL}/cart/add`, {
                method:  'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    customerId,
                    productId:   prod.id,
                    variantId:   normalizeVariantId(variant.id) !== 'default' ? variant.id : null,
                    variantName: variant.variantName || null,
                    name:        prod.productName,
                    img:         prod.image ? `${API_BASE_URL}/${prod.image}` : '',
                    price:       parseFloat(variant.salesPrice) || parseFloat(prod.salesPrice) || 0,
                    quantity:    1,
                    weight:      variantLabel,
                }),
            });
            if (!res.ok) console.error('Cart API failed:', res.status, await res.text());
            else window.dispatchEvent(new Event('cartUpdated'));
        } catch (err) { console.error('Cart API network error:', err.message); }
    };

    const handleToggleWishlist = async (e, product, variant) => {
        e.stopPropagation();

        // ── Guest check ──────────────────────────────────────────────────────
        const customerId = getCustomerId();
        if (!customerId) {
            showToast('Please login to save items to your wishlist!', 'warn');
            return;
        }

        let existingWishlist = JSON.parse(localStorage.getItem('wishlist')) || [];
        const isPresent      = existingWishlist.find(i => i.id === product.id);

        if (isPresent) {
            existingWishlist = existingWishlist.filter(i => i.id !== product.id);
            setWishlistIds(prev => prev.filter(id => id !== product.id));
            showToast('Removed from wishlist', 'info');
            try {
                const res  = await fetch(`${API_BASE_URL}/wishlist/${customerId}`);
                const data = await res.json();
                const item = data.find(i => i.productId === product.id);
                if (item) { await fetch(`${API_BASE_URL}/wishlist/${item.id}`, { method: 'DELETE' }); window.dispatchEvent(new Event('cartUpdated')); }
            } catch (err) { console.error(err); }
        } else {
            existingWishlist.push({ ...product, weight: getVariantLabel(variant), unit: variant?.unit || '' });
            setWishlistIds(prev => [...prev, product.id]);
            showToast('Added to wishlist!', 'success');
            try {
                await fetch(`${API_BASE_URL}/wishlist/add`, {
                    method:  'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        customerId, productId: product.id, name: product.productName,
                        img: product.image ? `${API_BASE_URL}/${product.image}` : '',
                        price: parseFloat(variant?.salesPrice || product.salesPrice) || 0,
                        weight: getVariantLabel(variant), stock: true,
                    }),
                });
                window.dispatchEvent(new Event('cartUpdated'));
            } catch (err) { console.error(err); }
        }
        localStorage.setItem('wishlist', JSON.stringify(existingWishlist));
        window.dispatchEvent(new Event('storage'));
    };

    const settings = {
        dots: false, arrows: true, infinite: products.length > 5,
        speed: 800, slidesToShow: 5, slidesToScroll: 1, autoplay: true,
        nextArrow: <SampleNextArrow />, prevArrow: <SamplePrevArrow />,
        responsive: [
            { breakpoint: 1399, settings: { slidesToShow: 4 } },
            { breakpoint: 992,  settings: { slidesToShow: 3 } },
            { breakpoint: 575,  settings: { slidesToShow: 2 } },
            { breakpoint: 424,  settings: { slidesToShow: 1 } },
        ],
    };

    if (loading || products.length === 0) return null;

    return (
        <section className="new-arrival pb-80">
            <style>{`
                /* ── Orange Arrows ── */
                .na-slick-arrow {
                    display: flex !important; align-items: center; justify-content: center;
                    width: 38px !important; height: 38px !important;
                    border-radius: 50% !important; border: 1.5px solid #F57C00 !important;
                    background: #fff !important; color: #F57C00 !important;
                    font-size: 18px; cursor: pointer;
                    transition: background 0.2s, color 0.2s, transform 0.2s !important; z-index: 2;
                }
                .na-slick-arrow:hover { background: #F57C00 !important; color: #fff !important; }
                .na-slick-arrow i { font-size: 18px; pointer-events: none; }
                .na-slick-arrow::before { display: none !important; }

                .na-toast { position:fixed; bottom:28px; right:28px; z-index:99999; display:flex; align-items:center; gap:12px; min-width:280px; padding:12px 16px; border-radius:12px; animation:naSlideIn 0.3s cubic-bezier(.4,0,.2,1) forwards; }
                .na-toast-success { background:#1B5E20; color:#fff; }
                .na-toast-info    { background:#0a4d92; color:#fff; }
                .na-toast-warn    { background:#92400E; color:#fff; }

                .na-card { background:#fff; border-radius:16px; border:1px solid #F0F0F0; overflow:hidden; transition:transform 0.22s,box-shadow 0.22s; display:flex; flex-direction:column; position:relative; }
                .na-card:hover { transform:translateY(-3px); box-shadow:0 12px 32px rgba(0,0,0,0.08); }
                .na-card__img-wrap { position:relative; background:#F7F7F7; height:170px; display:flex; align-items:center; justify-content:center; overflow:hidden; cursor:pointer; }
                .na-card__img-wrap img { width:80%; height:80%; object-fit:contain; transition:transform 0.3s; }
                .na-card:hover .na-card__img-wrap img { transform:scale(1.04); }
                .na-discount-badge { position:absolute; top:9px; left:9px; font-size:10px; font-weight:700; padding:2px 7px; border-radius:20px; color:#fff; }
                .na-wish-btn { position:absolute; top:9px; right:9px; border:none; background:#fff; border-radius:50%; width:30px; height:30px; display:flex; align-items:center; justify-content:center; font-size:16px; color:#9CA3AF; cursor:pointer; box-shadow:0 2px 8px rgba(0,0,0,0.10); transition:color 0.15s,transform 0.15s; z-index:2; }
                .na-wish-btn:hover { color:#EF4444; transform:scale(1.15); }
                .na-wish-btn.active { color:#EF4444; }
                .na-card__body { padding:13px 12px 12px; display:flex; flex-direction:column; flex-grow:1; gap:8px; }
                .na-title { font-size:13px; font-weight:600; color:#1A1A1A; text-align:center; height:2.7em; overflow:hidden; cursor:pointer; margin:0; line-height:1.35; }
                .na-stars { display:flex; justify-content:center; gap:2px; color:#F59E0B; font-size:11px; }
                .na-variants { display:flex; flex-wrap:wrap; gap:5px; justify-content:center; min-height:28px; }
                .na-vbtn { font-size:10px; font-weight:600; padding:4px 9px; border-radius:6px; cursor:pointer; transition:all 0.15s; outline:none; border:1px solid #E5E7EB; background:#fff; color:#6B7280; }
                .na-vbtn:hover { border-color:#9CA3AF; color:#374151; }
                .na-vbtn--active { border:1.5px solid #F57C00 !important; background:#FFF4EA !important; color:#C2410C !important; }
                .na-bottom { margin-top:auto; display:flex; flex-direction:column; gap:8px; }
                .na-price-row { display:flex; align-items:center; gap:6px; justify-content:center; flex-wrap:wrap; }
                .na-price-now { font-size:17px; font-weight:800; color:#1a1a1a; }
                .na-price-old { font-size:12px; color:#BDBDBD; text-decoration:line-through; }
                .na-save-pill { font-size:10px; font-weight:700; color:#166534; background:#DCFCE7; padding:2px 7px; border-radius:20px; white-space:nowrap; }
                .na-btn-cart { width:100%; color:#fff; border:none; padding:9px; border-radius:10px; font-size:12px; font-weight:600; cursor:pointer; display:flex; align-items:center; justify-content:center; gap:6px; transition:background 0.15s; }
                .na-section-heading { text-align:center; margin-bottom:28px; margin-top:70px; }
                .na-section-heading h5 { font-size:52px; font-weight:800; color:#111827; margin:0; letter-spacing:-0.5px; display:flex; align-items:center; justify-content:center; gap:10px; }
                .na-section-heading h5 span { color:#F57C00; }
                .na-section-heading p { font-size:15px; color:#9CA3AF; margin:6px 0 0; font-weight:400; }
                .na-heading-icon { font-size:28px; color:#F57C00; line-height:1; }
                .rc-stock-badge { font-size:10px; font-weight:700; padding:2px 7px; border-radius:20px; text-align:center; width:fit-content; margin:0 auto; }
                @keyframes naSlideIn { from{transform:translateX(110%);opacity:0} to{transform:translateX(0);opacity:1} }
                @media(max-width:768px){
                    .na-section-heading h5{font-size:28px;gap:6px;}
                    .na-section-heading p{font-size:13px;}
                    .na-heading-icon{font-size:20px;}
                }
                @media(max-width:480px){
                    .na-section-heading h5{font-size:22px;gap:4px;}
                    .na-section-heading p{font-size:12px;}
                    .na-heading-icon{font-size:16px;}
                }
            `}</style>

            {toast.show && (
                <div className={`na-toast na-toast-${toast.type}`}>
                    <span style={{ fontSize: 18 }}>
                        {toast.type === 'success' ? '✅' : toast.type === 'warn' ? '🔒' : '🔄'}
                    </span>
                    <div>
                        <div style={{ fontWeight: 700, fontSize: 13 }}>
                            {toast.type === 'warn' ? 'Login Required' : 'Done!'}
                        </div>
                        <div style={{ fontSize: 11, opacity: 0.85 }}>{toast.message}</div>
                    </div>
                </div>
            )}

            <div className="container container-lg">
                <div className="na-section-heading">
                    <h5>
                        <i className="ph-fill ph-shooting-star na-heading-icon" />
                        New <span>Arrivals</span>
                        <i className="ph-fill ph-shooting-star na-heading-icon" />
                    </h5>
                    <p>Fresh picks, just landed</p>
                </div>
                <div className="new-arrival__slider arrow-style-two">
                    <Slider {...settings}>
                        {products.map((product) => (
                            <ArrivalProductCard
                                key={product.id}
                                product={product}
                                wishlistIds={wishlistIds}
                                handleProductClick={handleProductClick}
                                handleAddToCart={handleAddToCart}
                                handleToggleWishlist={handleToggleWishlist}
                            />
                        ))}
                    </Slider>
                </div>
            </div>
        </section>
    );
};

export default NewArrivalOne;